/**
 * Pure filter/sort logic for MediaItems.
 *
 * Replaces the legacy filterFiles/sortFiles which parsed timestamps inline and
 * excluded the `to` day. Date range is inclusive of the whole `to` day.
 */
import type { MediaItem } from './types.ts';
import type { SortBy, MediaTypeFilter, DateRange } from '../state/galleryStore.ts';

export interface FilterOptions {
  search: string;
  dateRange: DateRange;
  mediaType: MediaTypeFilter;
}

function matchesMediaType(item: MediaItem, type: MediaTypeFilter): boolean {
  if (type === 'all') return true;
  if (type === 'images') return item.kind === 'image';
  return item.kind === 'video';
}

function matchesDate(item: MediaItem, range: DateRange): boolean {
  if (!range.from && !range.to) return true;
  if (item.timestamp == null) return false;
  if (range.from) {
    const from = new Date(range.from).getTime();
    if (Number.isFinite(from) && item.timestamp < from) return false;
  }
  if (range.to) {
    // Inclusive of the entire `to` day: add ~24h to the midnight value.
    const to = new Date(range.to).getTime() + 24 * 60 * 60 * 1000 - 1;
    if (Number.isFinite(to) && item.timestamp > to) return false;
  }
  return true;
}

export function filterItems(items: MediaItem[], opts: FilterOptions): MediaItem[] {
  const needle = opts.search.trim().toLowerCase();
  return items.filter((item) => {
    if (needle && !item.displayName.toLowerCase().includes(needle)) return false;
    if (!matchesMediaType(item, opts.mediaType)) return false;
    if (!matchesDate(item, opts.dateRange)) return false;
    return true;
  });
}

export function sortItems(items: MediaItem[], sortBy: SortBy, descending: boolean): MediaItem[] {
  const dir = descending ? -1 : 1;
  const sorted = [...items].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return dir * a.displayName.localeCompare(b.displayName);
      case 'size':
        return dir * ((a.size ?? 0) - (b.size ?? 0));
      case 'date':
      default: {
        // Items without a timestamp sort last regardless of direction.
        const at = a.timestamp ?? Number.NEGATIVE_INFINITY;
        const bt = b.timestamp ?? Number.NEGATIVE_INFINITY;
        return dir * (at - bt);
      }
    }
  });
  return sorted;
}

export function filterAndSort(
  items: MediaItem[],
  filter: FilterOptions,
  sortBy: SortBy,
  descending: boolean,
): MediaItem[] {
  return sortItems(filterItems(items, filter), sortBy, descending);
}
